// Shared presentation helpers for the server-side quote/breakdown (T6/T7).
// The engine emits one line per charge (entry / assessment / family_fee /
// late_surcharge / service_fee) with integer-cent amounts; these helpers only
// GROUP and FORMAT those server lines for display — no money is computed here
// beyond summing the emitted amounts.

export const money = (cents) =>
  "$" + ((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const sum = (lines) => lines.reduce((n, l) => n + (l.amount || 0), 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Rows for the payment-summary card, in the design's order:
// Entry fees · Family fees · School assessment · (late surcharge, anything
// else the engine emits). The service fee is rendered by the caller from
// breakdown.serviceCents, below the subtotal — amount only, rate stays private.
// With { add: true } (T9 — adding to an existing paid registration) the labels
// call out that only NEW additions are billed; the engine already excluded
// everything previously charged.
export function summarizeQuote(breakdown, { add = false } = {}) {
  const lines = (breakdown && breakdown.lines) || [];
  const entries = [], teamSeats = [], families = [], assessments = [], other = [];
  lines.forEach((l) => {
    // team seats are entry lines carrying meta.team (one per member per comp)
    if (l.type === "entry") (l.meta && l.meta.team ? teamSeats : entries).push(l);
    else if (l.type === "family_fee") families.push(l);
    else if (l.type === "assessment") assessments.push(l);
    else if (l.type !== "service_fee") other.push(l);
  });

  const rows = [];
  if (entries.length) rows.push({ label: add ? "New entry fees" : "Entry fees", amount: sum(entries) });
  if (teamSeats.length) {
    const teamCount = new Set(teamSeats.map((l) => l.meta.teamKey)).size;
    rows.push({
      label: add ? "New team entries" : "Team entries",
      amount: sum(teamSeats),
      sub: `${plural(teamCount, "team", "teams")} · ${plural(teamSeats.length, "seat", "seats")} (per competitor)`,
    });
  }

  if (families.length) {
    const waived = families.filter((l) => l.meta && l.meta.waived);
    const charged = families.filter((l) => !(l.meta && l.meta.waived));
    const bits = [];
    if (charged.length) bits.push(`${plural(charged.length, add ? "new family" : "family", add ? "new families" : "families")} × ${money(charged[0].amount)}`);
    if (waived.length) bits.push(`${plural(waived.length, "teacher family", "teacher families")} waived`);
    if (add) bits.push("existing not re-charged");
    rows.push({
      label: !add && families.length === 1 ? families[0].label : "Family fees",
      amount: sum(families),
      sub: bits.join(" · "),
    });
  }

  if (assessments.length) {
    const bits = [`${plural(assessments.length, add ? "new dancer" : "dancer", add ? "new dancers" : "dancers")} × ${money(assessments[0].amount)}`];
    if (add) bits.push("existing not re-charged");
    rows.push({
      label: "School assessment",
      amount: sum(assessments),
      sub: bits.join(" · "),
    });
  }

  other.forEach((l) => rows.push({ label: l.label, amount: l.amount, sub: l.meta && l.meta.detail }));
  return rows;
}
